// src/pages/RolesPage.tsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Check, Minus } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table';

const ROLES = [
  { id: 'owner', label: 'Owner' },
  { id: 'workspace_admin', label: 'Admin' },
  { id: 'developer', label: 'Developer' },
  { id: 'billing', label: 'Billing' },
];

const CAPABILITIES: { label: string; allowed: string[] }[] = [
  { label: 'Reassign team member roles', allowed: ['owner', 'workspace_admin'] },
  { label: 'Manage billing & payment method', allowed: ['owner', 'workspace_admin', 'billing'] },
];

const RolesPage: React.FC = () => {
  return (
    <div className="space-y-6">
      <PageHeader
        title="Roles & Permissions"
        description="What each role can do in this workspace."
        actions={
          <Link to="/team/members" className="inline-flex h-9 items-center gap-1.5 rounded-full border border-border px-4 text-[13px] font-medium text-foreground hover:bg-surface-raised">
            <ArrowLeft className="h-4 w-4" /> Back to Team
          </Link>
        }
      />

      <Panel title="Permissions by role" bodyClassName="px-0 pb-2">
        <Table minWidth={560}>
          <THead>
            <tr>
              <Th>Capability</Th>
              {ROLES.map((role) => (
                <Th key={role.id} align="center">{role.label}</Th>
              ))}
            </tr>
          </THead>
          <TBody>
            {CAPABILITIES.map((cap) => (
              <Tr key={cap.label}>
                <Td>{cap.label}</Td>
                {ROLES.map((role) => (
                  <Td key={role.id} align="center">
                    {cap.allowed.includes(role.id) ? (
                      <Check className="mx-auto h-4 w-4 text-success" aria-label="Allowed" />
                    ) : (
                      <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" aria-label="Not allowed" />
                    )}
                  </Td>
                ))}
              </Tr>
            ))}
          </TBody>
        </Table>
      </Panel>

      <p className="max-w-[70ch] text-sm text-muted-foreground">
        Every team member, whatever their role, can create and manage their own API keys and view usage analytics. These aren't currently role-restricted.
      </p>
    </div>
  );
};

export default RolesPage;
